import * as Device from 'expo-device';
import * as ScreenOrientation from 'expo-screen-orientation';
import React, { createContext, useContext, useEffect, useMemo } from 'react';
import {
  AppState,
  Platform,
  ScaledSize,
  useWindowDimensions,
} from 'react-native';
import { useMediaQuery } from 'react-responsive';

export enum LayoutSize {
  LARGE = 'LARGE',
  MEDIUM = 'MEDIUM',
  SMALL = 'SMALL',
  SMALL_TABLET = 'SMALL_TABLET',
}

export interface LayoutContextType {
  appDimensions: ScaledSize;
  drawerWidth: number;
  isLargeLayout: boolean;
  isMediumLayout: boolean;
  isSmallLayout: boolean;
  isSmallTabletLayout: boolean;
  layoutSize: LayoutSize;
}

export const LAYOUT_SIZE_TO_DRAWER_WIDTH_MAP: {
  [size in LayoutSize]: number;
} = {
  [LayoutSize.SMALL]: 0,
  [LayoutSize.SMALL_TABLET]: 300,
  [LayoutSize.MEDIUM]: 300,
  [LayoutSize.LARGE]: 72,
};

export const LayoutContext = createContext<LayoutContextType | null>(null);

export const useLayoutContext = () => {
  const context = useContext(LayoutContext);
  if (!context) {
    throw new Error('useLayoutContext requires an LayoutContextProvider');
  }

  return context;
};

export function useLayoutSizeValue<T>(
  valueMap: { [size in LayoutSize]: T },
  responsive = true,
) {
  const { layoutSize } = useLayoutContext();
  return responsive
    ? (valueMap[layoutSize] ?? valueMap[LayoutSize.SMALL])
    : valueMap[LayoutSize.SMALL];
}

export function useSmallLayoutSizeValue<T>(
  smallValue: T,
  otherValue: T,
  responsive = true,
) {
  const { isSmallLayout } = useLayoutContext();
  return !responsive || isSmallLayout ? smallValue : otherValue;
}

interface Props {
  children?: React.ReactNode;
}

const SMALL_MAX_WIDTH = 767;
const SMALL_TABLET_MAX_WIDTH = 960;
const LARGE_MIN_WIDTH = 1440;
/**
 * Compute layout size from a width value. Used as a fallback on native where
 * react-responsive's useMediaQuery doesn't work (no matchMedia API).
 */
const getLayoutSizeFromWidth = (width: number): LayoutSize => {
  if (width <= SMALL_MAX_WIDTH) {
    return LayoutSize.SMALL;
  }
  if (width <= SMALL_TABLET_MAX_WIDTH) {
    return LayoutSize.SMALL_TABLET;
  }
  if (width >= LARGE_MIN_WIDTH) {
    return LayoutSize.LARGE;
  }
  return LayoutSize.MEDIUM;
};

export const LayoutContextProvider: React.FC<Props> = ({ children }) => {
  const windowDimensions = useWindowDimensions();

  // react-responsive's useMediaQuery uses window.matchMedia which only works
  // on web. On native, all queries return false. We use these on web for
  // efficient resize detection, then fall back to windowDimensions on native.
  const isSmallMedia = useMediaQuery({ maxWidth: SMALL_MAX_WIDTH });
  const isSmallTabletMedia = useMediaQuery({
    maxWidth: SMALL_TABLET_MAX_WIDTH,
    minWidth: SMALL_MAX_WIDTH + 1,
  });
  // isMediumMedia not needed — MEDIUM is the fallback when no other matches.
  const isLargeMedia = useMediaQuery({ minWidth: LARGE_MIN_WIDTH });

  // Lock orientation to portrait for phone devices
  useEffect(() => {
    if (Platform.OS === 'web') {
      return;
    }

    const lockOrientation = async () => {
      try {
        await ScreenOrientation.lockAsync(
          ScreenOrientation.OrientationLock.PORTRAIT_UP,
        );
      } catch {
        // Do nothing
      }
    };

    if (Device.deviceType === Device.DeviceType.PHONE) {
      lockOrientation();
      // Re-lock on foreground. `expo-screen-orientation` drops the lock on
      // background.
      const subscription = AppState.addEventListener('change', (next) => {
        if (next === 'active') {
          lockOrientation();
        }
      });
      return () => subscription.remove();
    }
  }, []);

  const contextValue = useMemo<LayoutContextType>(() => {
    // On native, useMediaQuery returns false for everything. Use
    // windowDimensions.width directly to determine layout size.
    const isNative = Platform.OS !== 'web';
    const layoutSize = isNative
      ? getLayoutSizeFromWidth(windowDimensions.width)
      : isSmallMedia
        ? LayoutSize.SMALL
        : isSmallTabletMedia
          ? LayoutSize.SMALL_TABLET
          : isLargeMedia
            ? LayoutSize.LARGE
            : LayoutSize.MEDIUM;

    const isSmall = layoutSize === LayoutSize.SMALL;
    const isSmallTablet = layoutSize === LayoutSize.SMALL_TABLET;
    const isMedium = layoutSize === LayoutSize.MEDIUM;
    const isLarge = layoutSize === LayoutSize.LARGE;
    const drawerWidth = LAYOUT_SIZE_TO_DRAWER_WIDTH_MAP[layoutSize];

    return {
      appDimensions: isLarge
        ? { ...windowDimensions, width: windowDimensions.width - drawerWidth }
        : windowDimensions,
      drawerWidth,
      isLargeLayout: isLarge,
      isMediumLayout: isMedium,
      isSmallLayout: isSmall,
      isSmallTabletLayout: isSmallTablet,
      layoutSize,
    };
    // isMediumMedia excluded — MEDIUM is the fallback when no other matches.
  }, [isLargeMedia, isSmallMedia, isSmallTabletMedia, windowDimensions]);

  return (
    <LayoutContext.Provider value={contextValue}>
      {children}
    </LayoutContext.Provider>
  );
};
